import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";

const defaultBall = {
  innings: "team1",
  strikerId: "",
  nonStrikerId: "",
  bowlerId: "",
  runsOffBat: 0,
  extraType: "none", // none, wide, no-ball, bye, leg-bye, penalty
  extraRuns: 0,
  isLegalDelivery: true,
  wicket: {
    isWicket: false,
    kind: "",
    playerOutId: "",
    fielder: "",
  },
};

const CricketRealtimeTab = ({
  cricketMatches,
  onAddMatch,
  onEventsChange,
  buildMatchFromEvents,
  setActiveTab,
}) => {
  const [selectedMatchIndex, setSelectedMatchIndex] = useState(0);
  const [draftBall, setDraftBall] = useState(defaultBall);

  const selectedMatch = cricketMatches[selectedMatchIndex];
  const battingPlayers =
    selectedMatch?.[`${draftBall.innings}Players`] ?? [];
  const bowlingTeam = draftBall.innings === "team1" ? "team2" : "team1";
  const bowlingPlayers = selectedMatch?.[`${bowlingTeam}Players`] ?? [];

  const derivedSummary = useMemo(() => {
    if (!selectedMatch) return null;
    return buildMatchFromEvents(selectedMatch, selectedMatch.ballEvents || []);
  }, [selectedMatch, buildMatchFromEvents]);

  const handleAddBall = () => {
    if (!selectedMatch) return;
    if (!draftBall.strikerId || !draftBall.nonStrikerId || !draftBall.bowlerId) {
      toast.error("Select striker, non-striker and bowler");
      return;
    }

    // prevent selecting dismissed batters again
    const outSet = new Set(
      (selectedMatch.ballEvents || [])
        .filter((b) => b.innings === draftBall.innings && b.wicket?.isWicket)
        .map((b) => String(b.wicket.playerOutId || b.strikerId))
    );
    if (
      outSet.has(String(draftBall.strikerId)) ||
      outSet.has(String(draftBall.nonStrikerId))
    ) {
      toast.error("Selected batter is already out. Choose an available batter.");
      return;
    }

    // enforce bowler change after 6 legal deliveries in a spell
    const events = selectedMatch.ballEvents || [];
    let legalByBowlerThisSpell = 0;
    for (let i = events.length - 1; i >= 0; i--) {
      const b = events[i];
      if (b.innings !== draftBall.innings) break;
      if (String(b.bowlerId) !== String(draftBall.bowlerId)) break;
      const legal =
        b.isLegalDelivery ??
        (b.extraType !== "wide" && b.extraType !== "no-ball");
      if (legal) legalByBowlerThisSpell += 1;
      if (legalByBowlerThisSpell >= 6) break;
    }
    const isLegal =
      draftBall.isLegalDelivery ??
      (draftBall.extraType !== "wide" && draftBall.extraType !== "no-ball");
    if (isLegal && legalByBowlerThisSpell >= 6) {
      toast.error("Change bowler after 6 legal deliveries.");
      return;
    }

    const newEvents = [...(selectedMatch.ballEvents || []), draftBall];
    onEventsChange(selectedMatchIndex, newEvents);

    // swap strike on odd runs if legal delivery
    const shouldSwap =
      draftBall.isLegalDelivery &&
      ((draftBall.runsOffBat + Number(draftBall.extraRuns || 0)) % 2 === 1);

    setDraftBall((prev) => ({
      ...prev,
      strikerId: shouldSwap ? prev.nonStrikerId : prev.strikerId,
      nonStrikerId: shouldSwap ? prev.strikerId : prev.nonStrikerId,
      runsOffBat: 0,
      extraType: "none",
      extraRuns: 0,
      isLegalDelivery: true,
      wicket: { ...defaultBall.wicket },
    }));
  };

  const handleUndo = () => {
    if (!selectedMatch) return;
    const newEvents = [...(selectedMatch.ballEvents || [])];
    newEvents.pop();
    onEventsChange(selectedMatchIndex, newEvents);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-x-3">
          <button
            onClick={() => setActiveTab("summary")}
            className="text-blue-600 hover:underline"
          >
            ← Back to Summary
          </button>
          <button
            onClick={onAddMatch}
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg shadow-md transition-all duration-200"
          >
            Add Match
          </button>
        </div>
        <div className="text-sm text-gray-600">
          Record ball-by-ball and we auto-generate the summary.
        </div>
      </div>

      {cricketMatches.length === 0 && (
        <div className="p-6 bg-gray-50 border rounded-lg text-center text-gray-600">
          Add a match first, then start scoring in real-time.
        </div>
      )}

      {cricketMatches.length > 0 && (
        <>
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Match
                </label>
                <select
                  value={selectedMatchIndex}
                  onChange={(e) => setSelectedMatchIndex(Number(e.target.value))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {cricketMatches.map((m, idx) => (
                    <option key={idx} value={idx}>
                      Match #{idx + 1} - {m.team1Name || "Team 1"} vs{" "}
                      {m.team2Name || "Team 2"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Innings batting
                </label>
                <select
                  value={draftBall.innings}
                  onChange={(e) =>
                    setDraftBall((prev) => ({
                      ...prev,
                      innings: e.target.value,
                      strikerId: "",
                      nonStrikerId: "",
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="team1">
                    {selectedMatch?.team1Name || "Team 1"} batting
                  </option>
                  <option value="team2">
                    {selectedMatch?.team2Name || "Team 2"} batting
                  </option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleUndo}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded-lg"
                  disabled={(selectedMatch?.ballEvents || []).length === 0}
                >
                  Undo last delivery
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <SelectInput
                label="Striker"
                value={draftBall.strikerId}
                onChange={(v) => setDraftBall((p) => ({ ...p, strikerId: v }))}
                options={battingPlayers}
              />
              <SelectInput
                label="Non-Striker"
                value={draftBall.nonStrikerId}
                onChange={(v) => setDraftBall((p) => ({ ...p, nonStrikerId: v }))}
                options={battingPlayers}
              />
              <SelectInput
                label="Bowler"
                value={draftBall.bowlerId}
                onChange={(v) => setDraftBall((p) => ({ ...p, bowlerId: v }))}
                options={bowlingPlayers}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <NumberInput
                label="Runs off bat"
                value={draftBall.runsOffBat}
                onChange={(v) => setDraftBall((p) => ({ ...p, runsOffBat: v }))}
                min={0}
                max={6}
              />
              <SelectInput
                label="Extra type"
                value={draftBall.extraType}
                onChange={(v) =>
                  setDraftBall((p) => ({
                    ...p,
                    extraType: v,
                    isLegalDelivery: v !== "wide" && v !== "no-ball",
                  }))
                }
                options={[
                  { userId: "none", name: "None" },
                  { userId: "wide", name: "Wide" },
                  { userId: "no-ball", name: "No-ball" },
                  { userId: "bye", name: "Bye" },
                  { userId: "leg-bye", name: "Leg bye" },
                  { userId: "penalty", name: "Penalty" },
                ]}
              />
              <NumberInput
                label="Extra runs"
                value={draftBall.extraRuns}
                onChange={(v) => setDraftBall((p) => ({ ...p, extraRuns: v }))}
                min={0}
                max={6}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Legal delivery?
                </label>
                <input
                  type="checkbox"
                  checked={draftBall.isLegalDelivery}
                  onChange={(e) =>
                    setDraftBall((p) => ({
                      ...p,
                      isLegalDelivery: e.target.checked,
                    }))
                  }
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Counts to balls</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Wicket?
                </label>
                <input
                  type="checkbox"
                  checked={draftBall.wicket.isWicket}
                  onChange={(e) =>
                    setDraftBall((p) => ({
                      ...p,
                      wicket: { ...p.wicket, isWicket: e.target.checked },
                    }))
                  }
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Out on this ball</span>
              </div>
            </div>

            {draftBall.wicket.isWicket && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <SelectInput
                  label="Batter out"
                  value={draftBall.wicket.playerOutId}
                  onChange={(v) =>
                    setDraftBall((p) => ({
                      ...p,
                      wicket: { ...p.wicket, playerOutId: v },
                    }))
                  }
                  options={battingPlayers}
                />
                <SelectInput
                  label="How out"
                  value={draftBall.wicket.kind}
                  onChange={(v) =>
                    setDraftBall((p) => ({
                      ...p,
                      wicket: { ...p.wicket, kind: v },
                    }))
                  }
                  options={[
                    { userId: "bowled", name: "Bowled" },
                    { userId: "caught", name: "Caught" },
                    { userId: "lbw", name: "LBW" },
                    { userId: "run out", name: "Run out" },
                    { userId: "stumped", name: "Stumped" },
                  ]}
                />
                <TextInput
                  label="Fielder / notes"
                  value={draftBall.wicket.fielder}
                  onChange={(v) =>
                    setDraftBall((p) => ({
                      ...p,
                      wicket: { ...p.wicket, fielder: v },
                    }))
                  }
                  placeholder="Optional"
                />
              </div>
            )}

            <button
              onClick={handleAddBall}
            className="w-full max-w-[260px] bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5"
            >
              Record delivery
            </button>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <h4 className="text-lg font-semibold mb-2">Recorded deliveries</h4>
            {(selectedMatch?.ballEvents || []).length === 0 && (
              <p className="text-gray-500">No balls recorded yet.</p>
            )}
            {(selectedMatch?.ballEvents || []).length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left">#</th>
                      <th className="p-2 text-left">Innings</th>
                      <th className="p-2 text-left">Striker</th>
                      <th className="p-2 text-left">Bowler</th>
                      <th className="p-2 text-center">Runs</th>
                      <th className="p-2 text-center">Extras</th>
                      <th className="p-2 text-center">Legal</th>
                      <th className="p-2 text-left">Wicket</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedMatch?.ballEvents || []).map((ball, idx) => {
                      const batPlayersForBall =
                        selectedMatch?.[`${ball.innings}Players`] ?? [];
                      const bowlTeam = ball.innings === "team1" ? "team2" : "team1";
                      const bowlPlayersForBall =
                        selectedMatch?.[`${bowlTeam}Players`] ?? [];
                      return (
                        <tr key={idx} className="border-b">
                          <td className="p-2">{idx + 1}</td>
                          <td className="p-2 capitalize">{ball.innings}</td>
                          <td className="p-2">
                            {resolveName(batPlayersForBall, ball.strikerId)}
                          </td>
                          <td className="p-2">
                            {resolveName(bowlPlayersForBall, ball.bowlerId)}
                          </td>
                          <td className="p-2 text-center">{ball.runsOffBat}</td>
                          <td className="p-2 text-center">
                            {ball.extraType !== "none"
                              ? `${ball.extraType} (${ball.extraRuns})`
                              : "-"}
                          </td>
                          <td className="p-2 text-center">
                            {ball.isLegalDelivery ? "Yes" : "No"}
                          </td>
                          <td className="p-2">
                            {ball.wicket?.isWicket
                              ? `${resolveName(
                                  batPlayersForBall,
                                  ball.wicket.playerOutId
                                )} ${ball.wicket.kind}`
                              : "-"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {derivedSummary && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
              <h4 className="text-lg font-semibold text-blue-800 mb-2">
                Auto-generated summary (live preview)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {["team1", "team2"].map((teamKey) => (
                  <div key={teamKey} className="bg-white rounded-lg p-3 shadow-sm">
                    <div className="font-semibold text-gray-800 mb-1">
                      {teamKey === "team1"
                        ? derivedSummary.team1Name || "Team 1"
                        : derivedSummary.team2Name || "Team 2"}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {derivedSummary.innings[teamKey].totalRuns}/
                      {derivedSummary.innings[teamKey].wickets}
                    </div>
                    <div className="text-sm text-gray-600">
                      Overs {derivedSummary.innings[teamKey].overs}.
                      {derivedSummary.innings[teamKey].balls}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-blue-700 mt-2">
                Saving will include both ballEvents and the computed summary.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const resolveName = (players, id) =>
  players.find((p) => String(p.userId) === String(id))?.name || "Player";

const SelectInput = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 border rounded-lg"
    >
      <option value="">Choose...</option>
      {options.map((opt) => (
        <option key={opt.userId || opt.id} value={opt.userId || opt.id}>
          {opt.name || opt.full_name || opt.username}
        </option>
      ))}
    </select>
  </div>
);

const NumberInput = ({ label, value, onChange, min = 0, max = 6 }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value) || 0)))}
      className="w-full px-3 py-2 border rounded-lg"
    />
  </div>
);

const TextInput = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border rounded-lg"
    />
  </div>
);

export default CricketRealtimeTab;

